import React, { useState, useRef, useEffect } from 'react';
import {
  MoreHorizontal, Maximize2, Minimize2, RefreshCw,
  Copy, Download, ChevronRight
} from 'lucide-react';

interface PanelMenuProps {
  onExpand?: () => void;
  onMinimize?: () => void;
  onRefresh?: () => void;
  onCopy?: () => void;
  onExport?: (format: 'csv' | 'json') => void;
  isMinimized?: boolean;
}

export const PanelMenu: React.FC<PanelMenuProps> = ({
  onExpand,
  onMinimize,
  onRefresh,
  onCopy,
  onExport,
  isMinimized = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [showExportSubmenu, setShowExportSubmenu] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setShowExportSubmenu(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  // Close on escape
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsOpen(false);
        setShowExportSubmenu(false);
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen]);

  const handleMenuAction = (action: () => void) => {
    action();
    setIsOpen(false);
    setShowExportSubmenu(false);
  };

  const menuItems = [
    {
      icon: Maximize2,
      label: 'Expand',
      action: onExpand,
      show: !isMinimized && onExpand,
    },
    {
      icon: Minimize2,
      label: isMinimized ? 'Restore' : 'Minimize',
      action: onMinimize,
      show: onMinimize,
    },
    {
      icon: RefreshCw,
      label: 'Refresh Data',
      action: onRefresh,
      show: onRefresh,
    },
    {
      icon: Copy,
      label: 'Copy Data',
      action: onCopy,
      show: onCopy,
    },
  ];

  return (
    <div ref={menuRef} className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="min-w-[36px] min-h-[36px] md:min-w-0 md:min-h-0 flex items-center justify-center text-gray-600 hover:text-gray-400 active:bg-terminal-border/50 md:active:bg-transparent transition-colors md:p-0.5 rounded hover:bg-terminal-border"
        aria-label="Panel menu"
      >
        <MoreHorizontal size={16} className="md:w-3 md:h-3" />
      </button>

      {isOpen && (
        <div className="absolute right-0 top-full mt-1 w-44 md:w-36 bg-terminal-panel border border-terminal-border rounded shadow-lg z-50">
          {menuItems.filter(item => item.show).map((item, idx) => (
            <button
              key={idx}
              onClick={() => item.action && handleMenuAction(item.action)}
              className="w-full min-h-[44px] md:min-h-0 flex items-center gap-3 md:gap-2 px-3 md:px-2 py-2 md:py-1.5 text-xs md:text-[10px] text-gray-300 hover:bg-terminal-header hover:text-white active:bg-terminal-header/80 transition-colors"
            >
              <item.icon size={14} className="md:w-[11px] md:h-[11px]" />
              {item.label}
            </button>
          ))}

          {/* Export Submenu */}
          {onExport && (
            <div
              className="relative"
              onMouseEnter={() => setShowExportSubmenu(true)}
              onMouseLeave={() => setShowExportSubmenu(false)}
              onClick={() => setShowExportSubmenu(!showExportSubmenu)}
            >
              <button className="w-full min-h-[44px] md:min-h-0 flex items-center justify-between gap-3 md:gap-2 px-3 md:px-2 py-2 md:py-1.5 text-xs md:text-[10px] text-gray-300 hover:bg-terminal-header hover:text-white active:bg-terminal-header/80 transition-colors">
                <span className="flex items-center gap-3 md:gap-2">
                  <Download size={14} className="md:w-[11px] md:h-[11px]" />
                  Export
                </span>
                <ChevronRight size={12} className="md:w-[10px] md:h-[10px]" />
              </button>

              {showExportSubmenu && (
                <div className="absolute left-full top-0 ml-0.5 w-28 md:w-24 bg-terminal-panel border border-terminal-border rounded shadow-lg">
                  <button
                    onClick={() => handleMenuAction(() => onExport('csv'))}
                    className="w-full min-h-[44px] md:min-h-0 flex items-center gap-3 md:gap-2 px-3 md:px-2 py-2 md:py-1.5 text-xs md:text-[10px] text-gray-300 hover:bg-terminal-header hover:text-white active:bg-terminal-header/80 transition-colors"
                  >
                    CSV
                  </button>
                  <button
                    onClick={() => handleMenuAction(() => onExport('json'))}
                    className="w-full min-h-[44px] md:min-h-0 flex items-center gap-3 md:gap-2 px-3 md:px-2 py-2 md:py-1.5 text-xs md:text-[10px] text-gray-300 hover:bg-terminal-header hover:text-white active:bg-terminal-header/80 transition-colors"
                  >
                    JSON
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Divider */}
          <div className="border-t border-terminal-border my-1" />

          {/* Info */}
          <div className="px-3 md:px-2 py-1.5 md:py-1 text-[9px] md:text-[8px] text-gray-600">
            Drag to reorder
          </div>
        </div>
      )}
    </div>
  );
};

export default PanelMenu;
